import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { EditorModule } from 'primeng/editor';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { FieldsetModule } from 'primeng/fieldset';
import { ImageModule } from 'primeng/image';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { REFURBISHMENT_SOURCES, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingStockUnit } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel } from '../warehousing-item-display';

type StockUnitOption = WarehousingStockUnit & { label: string };

@Component({
  selector: 'app-warehousing-refurbishment-form',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, FieldsetModule,
    EditorModule, FileUploadModule, ImageModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule,
    InputNumberModule, InputTextModule, ProgressBarModule, PageFormSectionComponent, PageHeroComponent,
    PageShellComponent, SelectModule, TextareaModule, ToastModule
  ],
  providers: [MessageService],
  templateUrl: './warehousing-refurbishment-form.component.html'
})
export class WarehousingRefurbishmentFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private firebaseStorageService = inject(FirebaseStorageService);
  isLoading = false;
  isSaving = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Mark Refurbishment', 'Refurbishment', '/warehousing/refurbishment');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  stockUnits: StockUnitOption[] = [];
  supplierClients: CrmClient[] = [];
  refurbishmentSources = REFURBISHMENT_SOURCES;
  uploadedEvidenceUrls: string[] = [];
  evidenceUploadProgress = 0;
  evidenceUploadInProgress = false;
  refurbishmentForm = this.fb.group({
    stockUnitId: [null as number | null, Validators.required],
    sourceType: ['INTERNAL', Validators.required],
    supplierClientId: [null as number | null],
    estimatedCost: [null as number | null],
    actualCost: [null as number | null],
    notes: [''],
    startDate: [this.today()]
  });

  ngOnInit(): void {
    this.registerSourceTypeRules();
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;
    forkJoin({
      stockUnits: this.warehousingService.getStockUnits(),
      suppliers: this.crmService.searchClients({
        companyId: this.warehousingService.getCompanyId(),
        category: 'SUPPLIER',
        size: 500,
        sortBy: 'companyName',
        sortDir: 'asc'
      })
    }).subscribe({
      next: response => {
        this.stockUnits = (response.stockUnits.data || []).map(unit => ({
          ...unit,
          label: `${unit.unitCode || 'Unit ' + unit.id} - ${itemIdentityLabel(unit)}`
        }));
        this.supplierClients = response.suppliers.content || [];
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  createRefurbishment(): void {
    if (this.isSaving) {
      return;
    }
    if (this.evidenceUploadInProgress) {
      this.messageService.add({ severity: 'warn', summary: 'Upload in Progress', detail: 'Wait for evidence uploads to finish before saving.' });
      return;
    }
    if (this.refurbishmentForm.invalid) {
      this.refurbishmentForm.markAllAsTouched();
      return;
    }
    const value = this.refurbishmentForm.getRawValue();
    this.isSaving = true;
    this.warehousingService.createRefurbishment({
      stockUnitId: value.stockUnitId!,
      sourceType: value.sourceType as 'INTERNAL' | 'OUTSOURCED',
      supplierClientId: value.supplierClientId || undefined,
      estimatedCost: value.estimatedCost || undefined,
      actualCost: value.actualCost || undefined,
      notes: value.notes || undefined,
      photoUrls: this.uploadedEvidenceUrls.length ? this.uploadedEvidenceUrls.join('\n') : undefined,
      startDate: value.startDate || undefined
    }).pipe(finalize(() => this.isSaving = false)).subscribe(resp => {
      if (!resp.success) return;
      this.messageService.add({ severity: 'success', summary: 'Refurbishment Marked', detail: 'Stock remains counted and marked under refurbishment.' });
      this.router.navigate(['/warehousing/refurbishment/view', resp.data?.id]);
    });
  }

  discard(): void { this.router.navigate(['/warehousing/refurbishment']); }
  private today(): string { return new Date().toISOString().slice(0, 10); }

  supplierRequired(): boolean {
    return `${this.refurbishmentForm.value.sourceType || ''}`.toUpperCase() === 'OUTSOURCED';
  }

  uploadEvidence(event: FileUploadHandlerEvent): void {
    const files = event.files || [];
    if (!files.length) {
      return;
    }
    this.evidenceUploadInProgress = true;
    this.uploadNextEvidence(files, 0);
  }

  removeEvidence(url: string): void {
    this.uploadedEvidenceUrls = this.uploadedEvidenceUrls.filter(row => row !== url);
  }

  evidenceFileName(url: string): string {
    const clean = (url || '').split('?')[0];
    return decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1));
  }

  isImageUrl(url: string): boolean {
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((url || '').split('?')[0]);
  }

  private registerSourceTypeRules(): void {
    const supplierControl = this.refurbishmentForm.get('supplierClientId');
    this.refurbishmentForm.get('sourceType')?.valueChanges.subscribe(sourceType => {
      if (`${sourceType || ''}`.toUpperCase() === 'OUTSOURCED') {
        supplierControl?.setValidators([Validators.required]);
      } else {
        supplierControl?.clearValidators();
        supplierControl?.setValue(null, { emitEvent: false });
      }
      supplierControl?.updateValueAndValidity({ emitEvent: false });
    });
    supplierControl?.updateValueAndValidity({ emitEvent: false });
  }

  private uploadNextEvidence(files: File[], index: number): void {
    const file = files[index];
    if (!file) {
      this.evidenceUploadProgress = 0;
      this.evidenceUploadInProgress = false;
      return;
    }
    const companyId = this.warehousingService.getCompanyId();
    const path = `warehousing/refurbishment/${companyId}/evidence`;
    this.firebaseStorageService.uploadFileWithProgress(file, path).subscribe({
      next: upload => {
        this.evidenceUploadProgress = upload.progress;
        if (upload.url) {
          this.uploadedEvidenceUrls = [...this.uploadedEvidenceUrls, upload.url];
          this.messageService.add({ severity: 'success', summary: 'Evidence Uploaded', detail: `${file.name} uploaded.` });
          this.uploadNextEvidence(files, index + 1);
        }
      },
      error: err => {
        this.evidenceUploadProgress = 0;
        this.evidenceUploadInProgress = false;
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: err?.message || `${file.name} could not be uploaded.` });
      }
    });
  }
}
